import { Fragment } from "react"
import { useSelector } from "react-redux"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid"
import { WorkoutTemplateNames } from "functions/weeklyCalendarUtils"
import { GetMovementDifficultyScaleText, GetLoadDifficultyScaleText } from "functions/textUtils"

export default function PastWorkoutComponent({
    workoutTemplateKey,
    overviewFileCompletionData,
    workoutCompletionData,
    isPresent = false
}) {

    const baseWorkoutLength = {
        'Warm Up': 3,
        'Prep': 4,
        'Main 1': 2,
        'Main 2': 2,
        'Accessory 1': 2
    }

    const profileType = useSelector(state => state.trainingChart.profileType)
    const trainingChartPhaseFilter = useSelector(state => state.trainingChart.filters.phaseFilter)

    if (overviewFileCompletionData === null || workoutCompletionData === null) return null

    const isCompleted = overviewFileCompletionData[workoutTemplateKey].completed

    if (profileType === 'group') {
        return (
            <div className="rounded-lg p-4 flex flex-col justify-center items-center text-sm text-center space-y-2 bg-gray-200">
                {WorkoutTemplateNames[workoutTemplateKey]} Group Data Coming Soon
            </div>
        )
    }

    return (
        <div className={`
            flex flex-col border border-gray-400 divide-y divide-gray-400 rounded-lg overflow-hidden
            ${isPresent ? 'outline outline-[2px] outline-orange-300 outline-offset-[-1px]' : ''}
        `}>
            <div className="p-4 flex flex-col justify-center items-center text-sm text-center space-y-2 bg-gray-200">
                {isCompleted === false && <XCircleIcon className="h-6 text-red-400" />}
                {isCompleted === true && <CheckCircleIcon className="h-6 text-green-400" />}
                <div>{WorkoutTemplateNames[workoutTemplateKey]}</div>
            </div>

            {workoutCompletionData.phases.map((phase, phaseIndex) => (
                <Fragment key={phaseIndex}>
                    {trainingChartPhaseFilter.includes(phase.name) && (
                        <div>
                            <div className="w-full flex items-center p-2 bg-gray-100 border-b border-gray-400">
                                <div className="text-xs flex-grow">{phase.name}</div>
                                <div className="text-xs text-gray-600">{phase.circuitRounds || 0} set{phase.circuitRounds === 1 ? '' : 's'}</div>
                            </div>

                            <div className="divide-y divide-gray-400">
                                {phase.exercises.map((exercise, exerciseIndex) => (
                                    <div
                                        key={exerciseIndex}
                                        className='h-24 flex flex-col overflow-y-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
                                    >
                                        <div className="my-auto p-2 space-y-1 text-center">
                                            <div className="text-xs pb-1 font-semibold text-gray-600">{exercise.name}</div>

                                            {exercise.sets.map((set, setIndex) => (
                                                <div key={setIndex}>
                                                    {(
                                                        overviewFileCompletionData[workoutTemplateKey].completed &&
                                                        set.completedSetData &&
                                                        set.completedSetData.completed
                                                    ) === true &&
                                                        <div className="flex items-center justify-center gap-x-1">
                                                            <div>
                                                                <CheckCircleIcon className="h-4 text-green-400" />
                                                            </div>

                                                            {exercise.setUnit === 'reps' &&
                                                                <>
                                                                    {exercise.exerciseType === 'bodyweight' &&
                                                                        <>
                                                                            <div className='text-xs'>{set.completedSetData.setUnitsCompleted} / {set.reps} {exercise.lateral === 'unilateral' ? ' per side' : ''}</div>
                                                                            <div className="text-xs">({GetMovementDifficultyScaleText(set.completedSetData.movementDifficulty)})</div>
                                                                        </>
                                                                    }

                                                                    {exercise.exerciseType === 'recommendation' &&
                                                                        <>
                                                                            <div className="text-xs">{set.completedSetData.setUnitsCompleted} / {set.reps} {exercise.lateral === 'unilateral' ? ' per side' : ''}</div>
                                                                            <div className="text-xs">{set.recommendationData.weightRecommendationRounded}lbs</div>
                                                                            <div className="text-xs">({GetLoadDifficultyScaleText(set.completedSetData.difficulty)})</div>
                                                                        </>
                                                                    }
                                                                </>
                                                            }

                                                            {exercise.setUnit === 'seconds' &&
                                                                <>
                                                                    <div className='text-xs'>{set.completedSetData.setUnitsCompleted} / {set.seconds}s {exercise.lateral === 'unilateral' ? ' per side' : ''}</div>
                                                                    <div className="text-xs">({GetMovementDifficultyScaleText(set.completedSetData.movementDifficulty)})</div>
                                                                </>
                                                            }
                                                        </div>
                                                    }

                                                    {(
                                                        overviewFileCompletionData[workoutTemplateKey].completed &&
                                                        set.completedSetData &&
                                                        set.completedSetData.completed
                                                    ) === false &&
                                                        <div className="flex items-center justify-center gap-x-1">
                                                            <div>
                                                                <XCircleIcon className="h-4 text-red-400" />
                                                            </div>

                                                            {exercise.setUnit === 'reps' &&
                                                                <div className="text-xs">-- / {set.reps} {exercise.lateral === 'unilateral' ? ' per side' : ''}</div>
                                                            }

                                                            {exercise.setUnit === 'seconds' &&
                                                                <div className="text-xs">-- / {set.seconds}s {exercise.lateral === 'unilateral' ? ' per side' : ''}</div>
                                                            }
                                                        </div>
                                                    }

                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {/* Shorter workout length skipped this exercise */}
                                {phase.exercises.length < baseWorkoutLength[phase.name] &&
                                    <>
                                        {Array.from({ length: baseWorkoutLength[phase.name] - phase.exercises.length }).map((_, index) => (
                                            <div key={index} className="h-24 p-2 flex flex-col justify-center items-center text-center space-y-1 overflow-y-scroll bg-gray-50">
                                                <div className="text-xs text-gray-600">Shorter workout length skipped this exercise</div>
                                            </div>
                                        ))}
                                    </>
                                }
                            </div>


                        </div>
                    )}
                </Fragment>
            ))}

            {/* Shorter workout missing Accessory 1 entirely */}
            {(workoutTemplateKey === 'strength-session-a' || workoutTemplateKey === 'strength-session-b') && workoutCompletionData.phases.length === 4 &&
                <div>
                    <div className="w-full flex items-center px-4 py-2 bg-gray-50 border-b border-gray-400">
                        <div className="text-sm flex-grow">Accessory 1</div>
                    </div>
                
                    <div className="h-24 p-2 flex flex-col justify-center items-center text-center space-y-1 overflow-y-scroll bg-gray-50 border-b border-gray-400">
                        <div className="text-xs text-gray-600">Shorter workout length skipped this exercise</div>
                    </div> 

                    <div className="h-24 p-2 flex flex-col justify-center items-center text-center space-y-1 overflow-y-scroll bg-gray-50">
                        <div className="text-xs text-gray-600">Shorter workout length skipped this exercise</div>
                    </div> 
                </div>
            }
        </div>
    )
}